import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Download, Headphones, Laptop, Monitor, Radio, Terminal, Users } from 'lucide-react';

const GaragePage: React.FC = () => {
  return (
    <div className="flex flex-col">
      {/* Hero */}
      <section className="relative overflow-hidden py-16 md:py-24">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-950 to-black"></div>
        <div className="absolute -top-24 -left-24 w-72 h-72 rounded-full bg-purple-600/20 blur-3xl"></div>
        <div className="absolute -bottom-24 -right-24 w-72 h-72 rounded-full bg-indigo-600/20 blur-3xl"></div>

        <div className="container mx-auto px-4 relative z-10 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-sm uppercase tracking-[0.3em] text-purple-300/80 mb-4">
              Just Jam Garage
            </p>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              The realtime rehearsal space for your next jam
            </h1>
            <p className="text-lg text-gray-300 mb-8 max-w-xl">
              Just Jam Garage pairs a dedicated audio room service with a desktop client so you can
              rehearse together, even when your band is miles apart. Sign in with your JustJam
              account, pick a room, and start playing.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative"
          >
            <div className="relative rounded-2xl border border-gray-800 bg-gray-900/60 p-8 shadow-2xl">
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-purple-600/10 via-transparent to-indigo-600/10"></div>
              <img
                src="/img/justjam-garage.png"
                alt="Band rehearsing in a studio"
                className="relative z-10 w-full h-64 md:h-80 object-cover rounded-xl"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-gray-900/90 border border-gray-800 rounded-xl px-4 py-3 text-sm text-gray-300 shadow-lg">
              Live rooms for your bandmates
            </div>
          </motion.div>
        </div>
      </section>

      {/* Downloads */}
      <section className="py-16 bg-gray-950">
        <div className="container mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Download Garage</h2>
            <p className="text-lg text-gray-400 max-w-3xl mx-auto">
              Install the desktop app, sign in with your JustJam account, and join or create a room.
              The audio goes directly from your instrument to your bandmates in realtime.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
            {[
              {
                title: 'Windows',
                text: 'Recommended for Windows 10 or later.',
                icon: <Monitor className="w-6 h-6 text-purple-400" />,
                href: '/download/justjam-garage-client-windows.zip',
              },
              {
                title: 'macOS',
                text: 'Supports Apple silicon and Intel Macs.',
                icon: <Laptop className="w-6 h-6 text-purple-400" />,
                href: '/download/justjam-garage-client-macos.zip',
              },
              {
                title: 'Linux',
                text: 'Tested on Ubuntu and Fedora.',
                icon: <Terminal className="w-6 h-6 text-purple-400" />,
                href: '/download/justjam-garage-client-linux.tar.gz',
              },
            ].map((card) => (
              <div
                key={card.title}
                className="bg-gray-900/80 border border-gray-800 rounded-xl p-6 flex flex-col"
              >
                <div className="w-12 h-12 rounded-full bg-purple-500/10 flex items-center justify-center mb-4">
                  {card.icon}
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{card.title}</h3>
                <p className="text-gray-400 mb-6">{card.text}</p>
                <a
                  href={card.href}
                  className="mt-auto inline-flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-md transition-colors duration-300"
                >
                  <Download className="w-4 h-4" />
                  Download
                </a>
              </div>
            ))}
          </div>

          <div className="max-w-3xl mx-auto text-center text-gray-300">
            <p>
              Once installed, open Garage, sign in, and pick a room name. Share it with your band so
              everyone can join the same session.
            </p>
          </div>
        </div>
      </section>

      {/* Details */}
      <section className="py-16 bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">How Garage fits into JustJam</h2>
            <p className="text-lg text-gray-400 max-w-3xl mx-auto">
              Garage is the companion space for musicians who want to rehearse between in-person jam
              sessions. It keeps the experience simple, focused, and connected to your JustJam
              identity.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                title: 'Desktop Client',
                text: 'A dedicated app that captures your instrument audio and connects to your room.',
                icon: <Laptop className="w-6 h-6 text-purple-400" />,
              },
              {
                title: 'Shared Rooms',
                text: 'Create or join a room name to rehearse together with the musicians you meet on JustJam.',
                icon: <Radio className="w-6 h-6 text-purple-400" />,
              },
              {
                title: 'Band Presence',
                text: 'Use the same JustJam account so everyone in the session knows who is playing.',
                icon: <Users className="w-6 h-6 text-purple-400" />,
              },
            ].map((card, index) => (
              <motion.div
                key={card.title}
                className="bg-gray-800/70 border border-gray-700 rounded-xl p-6"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="w-12 h-12 rounded-full bg-purple-500/10 flex items-center justify-center mb-4">
                  {card.icon}
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{card.title}</h3>
                <p className="text-gray-300">{card.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
};

export default GaragePage;
